import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';
import { useCookies } from 'react-cookie';

import { useSettings } from '@/data/Settings';

import { FBC_MAX_AGE } from './constants';
import { FacebookClickIdentifiers } from './types';
import { extractFbclidFromFbc, isFbcExpired } from './utils';

// Resolves the fbp/fbc parameters sent to Meta's Conversions API. The Meta
// Pixel is injected via GTM (can be blocked by adblockers), so _fbc is derived
// here from the `fbclid` URL query param instead of relying on the Pixel to
// have written the _fbc cookie first. The value is computed synchronously
// during render so it's available on the very first render, then persisted to
// the _fbc cookie (90-day window) for later visits.
// See https://developers.facebook.com/documentation/ads-commerce/conversions-api/parameters/fbp-and-fbc/
export const useFacebookClickIdentifiers = (): FacebookClickIdentifiers => {
	const [cookies, setCookie, removeCookie] = useCookies(['_fbc', '_fbp']);
	const searchParams = useSearchParams();
	const { settings } = useSettings();
	const domain = settings.userData?.FACEBOOK_COOKIE_DOMAIN?.trim() || undefined;

	const fbclid = searchParams?.get('fbclid');

	const currentFbc = cookies._fbc;
	const fbcExpired = !!currentFbc && isFbcExpired(currentFbc);
	const existingFbclid = currentFbc && !fbcExpired ? extractFbclidFromFbc(currentFbc) : null;

	const generatedFbcRef = useRef<{ fbclid: string; fbc: string }>();

	if (fbclid && generatedFbcRef.current?.fbclid !== fbclid) {
		generatedFbcRef.current = { fbclid, fbc: `fb.1.${Date.now()}.${fbclid}` };
	}

	const computedFbc = useMemo(() => {
		if (fbclid && fbclid !== existingFbclid) {
			return generatedFbcRef.current?.fbc;
		}

		return !fbcExpired ? currentFbc : undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fbclid, existingFbclid, fbcExpired, currentFbc]);

	useEffect(() => {
		if (currentFbc && fbcExpired) {
			removeCookie('_fbc', { path: '/' });
			if (domain) removeCookie('_fbc', { path: '/', domain });
			return;
		}

		if (fbclid && fbclid !== existingFbclid && computedFbc) {
			if (domain) removeCookie('_fbc', { path: '/' });
			setCookie('_fbc', computedFbc, {
				path: '/',
				maxAge: FBC_MAX_AGE,
				...(domain && { domain }),
			});
		}
	}, [
		currentFbc,
		fbcExpired,
		fbclid,
		existingFbclid,
		computedFbc,
		domain,
		setCookie,
		removeCookie,
	]);

	return {
		fbp: cookies._fbp,
		fbc: computedFbc,
	};
};
